// NEXT_PUBLIC_API_URL is inlined at BUILD time, so changing it in the hosting
// dashboard has no effect until the frontend is rebuilt. The fallback below
// exists so a missing variable does not break the app outright — but it is a
// hazard: the previous fallback pointed at a Railway service that had been
// deleted, so every request 404'd silently for months. Keep it pointed at the
// live backend, and prefer setting NEXT_PUBLIC_API_URL explicitly.
export const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL ??
  "https://prosa-api-production.up.railway.app";

export const TOKEN_STORAGE_KEY = "saas_tool_token";

export function getStoredToken(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  return window.localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setStoredToken(token: string) {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

export function clearStoredToken() {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.removeItem(TOKEN_STORAGE_KEY);
}

export async function fetchWithAuth(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const token = getStoredToken();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  const method = init.method?.toUpperCase() ?? "GET";
  if (method !== "GET" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    cache: "no-store",
    mode: "cors",
    ...init,
    headers,
  });

  // A 401 on a request that CARRIED a token means the session expired or was
  // revoked (tokens live ~1 hour with no refresh). Without this, every data
  // call after expiry failed silently per-component — only /auth/me handled
  // it. A 401 with no token attached is left to the caller: it just means
  // "not signed in", which public pages handle themselves.
  if (response.status === 401 && token && typeof window !== "undefined") {
    clearStoredToken();
    if (window.location.pathname !== "/login") {
      window.location.assign("/login?session=expired");
    }
  }

  return response;
}

